import React, { useState, useEffect } from "react";
import { Box, Paper, Typography, TextField, Button, Grid } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_LOCATION,
  CREATE_LOCATION,
  UPDATE_LOCATION,
  LIST_LOCATIONS,
} from "../../graphql/operations";
import { LocationInput } from "../../types/location";
import { Helmet } from "react-helmet-async";

const LocationForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState<LocationInput>({
    name: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    phone: "",
    email: "",
    manager: "",
    capacity: 0,
  });

  const { data, loading } = useQuery(GET_LOCATION, {
    variables: { id },
    skip: !isEdit,
  });

  // const [createLocation] = useMutation(CREATE_LOCATION);
  const [createLocation] = useMutation(CREATE_LOCATION, {
    refetchQueries: [{ query: LIST_LOCATIONS }],
    onCompleted: () => {
      navigate("/dashboard/locations");
    },
  });

  const [updateLocation] = useMutation(UPDATE_LOCATION);

  useEffect(() => {
    if (data?.getLocation) {
      setFormData(data.getLocation);
    }
  }, [data]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEdit) {
        await updateLocation({
          variables: {
            id: formData.id,
            input: {
              name: formData.name,
              address: formData.address,
              city: formData.city,
              state: formData.state,
              zipCode: formData.zipCode,
              country: formData.country,
              phone: formData.phone,
              email: formData.email,
              manager: formData.manager,
              capacity: formData.capacity,
              // ONLY include the fields that are in UpdateEmployeeInput
            },
          },
        });
      } else {
        await createLocation({
          variables: { input: formData },
        });
      }
      navigate("/dashboard/locations");
    } catch (error) {
      console.error("Error saving location:", error);
    }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Helmet>
        <title>
          Almond Tree Dashboard - {isEdit ? "Update" : "Create"} Location
        </title>
      </Helmet>
      <Typography variant="h4" gutterBottom>
        {isEdit ? "Edit Location" : "Add Location"}
      </Typography>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                required
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) =>
                  setFormData({ ...formData, city: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                onChange={(e) =>
                  setFormData({ ...formData, state: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={formData.zipCode}
                onChange={(e) =>
                  setFormData({ ...formData, zipCode: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={(e) =>
                  setFormData({ ...formData, country: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="email"
                label="Email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Manager"
                value={formData.manager}
                onChange={(e) =>
                  setFormData({ ...formData, manager: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Capacity"
                value={formData.capacity}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    capacity: parseInt(e.target.value),
                  })
                }
                required
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{ backgroundColor: "#919749" }}
            >
              {isEdit ? "Update" : "Create"} Location
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate("/dashboard/locations")}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default LocationForm;
